/**
 * @module  public/js/modals/goModal
 * @summary Simple modal to capture "Go" reasons.
 *
 * @param {object} [opts]
 * @param {boolean} [opts.allowEmailComposer=false] - Whether email reasons can open the email composer.
 * @param {boolean} [opts.showEmailButton] - Legacy email CTA flag (maps to allowEmailComposer).
 * @param {boolean} [opts.showTaskButton=true] - Whether to show the Save & Task CTA.
 * @returns {Promise<
 *   | {
 *       meetingInvite: boolean;
 *       meetingBooked: boolean;
 *       eventInvite: boolean;
 *       other: string;
 *       selectedReasons: Array<{ id: string; label: string; text?: string }>;
 *       emailComposerRequested: boolean;
 *       action?: 'save' | 'email' | 'task';
 *     }
 *   | null
 * >}
 */

import { applyFocusModeTooltips } from '../focusmode/tooltipService.js';

const DEFAULT_GO_REASONS = [
  {
    id: 'r_email',
    label: 'Email',
    tooltip: 'Record email follow-up as part of this GO outcome.',
    kind: 'email',
    enabled: true,
    behavior: { openEmailComposer: false, meetingBooked: false },
  },
  {
    id: 'r_meeting',
    label: 'Meeting Invite Sent',
    tooltip: 'Log that a meeting invite was sent or a meeting was booked.',
    kind: 'check',
    enabled: true,
    behavior: { openEmailComposer: false, meetingBooked: true },
    legacyMap: 'meetingInviteSent',
  },
  {
    id: 'r_event',
    label: 'Event Invite Sent',
    tooltip: 'Log that an event invite was sent.',
    kind: 'check',
    enabled: true,
    legacyMap: 'eventInviteSent',
  },
  {
    id: 'r_other',
    label: 'Other',
    tooltip: 'Add another GO reason in your own words.',
    kind: 'text',
    enabled: true,
    placeholder: 'Specify…',
    legacyMap: 'goOtherText',
  },
];
import { normalizeGoReasons } from '../utils/goReasons.js';

/**
 * Escape text for safe HTML rendering.
 *
 * @param {string} value - Raw text input.
 * @returns {string} HTML-safe string.
 */
function escapeHtml(value) {
  return `${value ?? ''}`
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Build a deterministic DOM-safe identifier from the provided value.
 *
 * @param {string} value - Raw identifier value.
 * @returns {string} Safe identifier string.
 */
function toDomId(value) {
  const cleaned = `${value ?? ''}`.trim().replace(/[^a-zA-Z0-9_-]/g, '-');
  return cleaned || 'go-reason';
}

export async function openGoModal({
  allowEmailComposer,
  showEmailButton,
  showTaskButton = true,
  enableTooltips,
} = {}) {
  const di = (await import('../bootstrap/app.js')).default;
  const modalService = di.modalService || di.modal;
  const { fsService } = di;
  const composerAllowed =
    allowEmailComposer !== undefined
      ? allowEmailComposer
      : showEmailButton !== undefined
        ? showEmailButton
        : false;
  const tooltipsEnabled =
    enableTooltips ??
    document.body?.getAttribute('data-focus-tooltips') !== 'false';

  let goReasons = DEFAULT_GO_REASONS;
  try {
    const config = await fsService.getDocument('AppConfig/outcomes');
    const reasons = normalizeGoReasons(config?.goReasons || [], {
      mode: 'runtime',
      warn: console.warn,
    });

    if (!config?.goReasons || config.goReasons.length === 0) {
      await fsService
        .setDocument(
          'AppConfig/outcomes',
          { goReasons: DEFAULT_GO_REASONS },
          { merge: true }
        )
        .catch((err) =>
          console.warn('[GoModal] failed to seed /AppConfig/outcomes', err)
        );
    }

    if (reasons.length > 0) goReasons = reasons;
  } catch (err) {
    console.warn('[GoModal] failed to load /AppConfig/outcomes', err);
  }

  const enabledReasons = goReasons.filter(
    (reason) => reason.enabled !== false && reason.label
  );

  let host = document.getElementById('goModal');
  if (!host) {
    host = document.createElement('div');
    host.id = 'goModal';
    host.className = 'hidden modal-overlay';
    document.body.appendChild(host);
    modalService.register('goModal', host);
  }

  host.innerHTML = /* html */ `
    <div class="modal-container modal-grid modal-auto modal-teal p-6 focus-choice-modal">
      <header class="focus-modal__header mb-4">
        <div class="focus-modal__title-group">
          <h3>Select a “Go” reason</h3>
        </div>
      </header>
      <form id="goForm" class="reason-list focus-choice-modal__panel">
        ${enabledReasons
          .map((reason) => {
            const label = escapeHtml(reason.label);
            const reasonId = escapeHtml(reason.id);
            const reasonLabel = escapeHtml(reason.label);
            const legacyMap = escapeHtml(reason.legacyMap || '');
            const domId = toDomId(reason.id);
            const tooltip = escapeHtml(reason.tooltip || '');
            const tooltipAttr = tooltip
              ? ` data-focus-tooltip="${tooltip}"`
              : '';
            const checkbox = `
        <label class="reason-btn ui-select-surface"${tooltipAttr}>
          <input
            type="checkbox"
            name="reason-${domId}"
            data-reason-id="${reasonId}"
            data-reason-label="${reasonLabel}"
            data-legacy-map="${legacyMap}"
            data-kind="${reason.kind}"
          >
          <span>${label}</span>
        </label>`;

            if (reason.kind === 'text') {
              return `${checkbox}
        <input
          id="goText-${domId}"
          name="reasonText-${domId}"
          class="input w-full mt-2 hidden"
          placeholder="${escapeHtml(reason.placeholder || 'Specify…')}"
          data-reason-text="${reasonId}"
          aria-hidden="true"
        />`;
            }

            return checkbox;
          })
          .join('')}
      </form>
      <footer class="focus-modal__footer focus-choice-modal__footer">
        <div
          class="focus-modal__footer-actions"
          data-actions="1"
        >
          <button
            id="goCancel"
            type="button"
            class="btn-pill btn--grey btn--info-sm btn--secondary-action"
            data-focus-tooltip="Close without saving a GO reason."
          >
            Cancel
          </button>
        </div>
        <div
          class="focus-modal__footer-actions"
          data-actions="${showTaskButton ? '2' : '1'}"
        >
          ${
            showTaskButton
              ? `<button id="goTask" type="button" class="btn-pill btn--teal btn--info-sm btn--primary-action focus-modal__alternate-action" data-focus-tooltip="Save this GO outcome and create a follow-up task.">Save & Task</button>`
              : ''
          }
          <button id="goOk" type="button" class="btn-pill btn--teal btn--info-sm btn--primary-action" data-focus-tooltip="Save this GO outcome and move on.">Save</button>
        </div>
      </footer>
    </div>`;

  applyFocusModeTooltips(host, { enableTooltips: tooltipsEnabled });

  return new Promise((resolve) => {
    const reasonLookup = new Map(
      enabledReasons.map((reason) => [reason.id, reason])
    );
    const form = host.querySelector('#goForm');
    const btnOk = host.querySelector('#goOk');
    const btnCancel = host.querySelector('#goCancel');
    const btnTask = host.querySelector('#goTask');
    const textInputs = Array.from(form.querySelectorAll('[data-reason-text]'));
    const checkboxInputs = Array.from(
      form.querySelectorAll('input[type="checkbox"][data-reason-id]')
    );
    const textToggleHandlers = new Map();

    /**
     * Toggle text input visibility for the provided checkbox.
     *
     * @param {HTMLInputElement} input - Checkbox input element.
     */
    const toggleTextInput = (input) => {
      const targetId = input.dataset.reasonId;
      const textInput = form.querySelector(`[data-reason-text="${targetId}"]`);
      if (!textInput) return;
      const shouldShow = input.checked;
      textInput.classList.toggle('hidden', !shouldShow);
      textInput.toggleAttribute('aria-hidden', !shouldShow);
      if (!shouldShow) textInput.value = '';
    };

    checkboxInputs.forEach((input) => {
      input.checked = false;
      if (input.dataset.kind === 'text') {
        const handler = () => toggleTextInput(input);
        textToggleHandlers.set(input, handler);
        input.addEventListener('change', handler);
        toggleTextInput(input);
      }
    });

    textInputs.forEach((input) => {
      input.value = '';
      input.classList.add('hidden');
      input.setAttribute('aria-hidden', 'true');
    });

    const close = (val) => {
      modalService.close('goModal');
      btnOk.removeEventListener('click', onOk);
      btnCancel.removeEventListener('click', onCancel);
      btnTask?.removeEventListener('click', onTask);
      textToggleHandlers.forEach((handler, input) => {
        input.removeEventListener('change', handler);
      });
      resolve(val);
    };

    const buildPayload = () => {
      const selectedReasons = [];
      enabledReasons.forEach((reason) => {
        const checkbox = form.querySelector(
          `input[type="checkbox"][data-reason-id="${reason.id}"]`
        );
        const isSelected = checkbox?.checked;
        if (!isSelected) return;
        const selectedReason = { id: reason.id, label: reason.label };
        if (reason.kind === 'text') {
          const input = form.querySelector(`[data-reason-text="${reason.id}"]`);
          selectedReason.text = `${input?.value ?? ''}`.trim();
        }
        selectedReasons.push(selectedReason);
      });

      const emailReasonSelected = selectedReasons.some((selectedReason) => {
        const reason = reasonLookup.get(selectedReason.id);
        return reason?.kind === 'email' && reason?.behavior?.openEmailComposer;
      });
      const shouldCompose = emailReasonSelected && composerAllowed;
      const meetingBookedSelected = selectedReasons.some((selectedReason) => {
        const reason = reasonLookup.get(selectedReason.id);
        return reason?.behavior?.meetingBooked;
      });

      const legacyMeeting = selectedReasons.some((selectedReason) => {
        const reason = enabledReasons.find(
          (item) => item.id === selectedReason.id
        );
        return reason?.legacyMap === 'meetingInviteSent';
      });
      const legacyEvent = selectedReasons.some((selectedReason) => {
        const reason = enabledReasons.find(
          (item) => item.id === selectedReason.id
        );
        return reason?.legacyMap === 'eventInviteSent';
      });
      const legacyOtherReason = selectedReasons.find((selectedReason) => {
        const reason = enabledReasons.find(
          (item) => item.id === selectedReason.id
        );
        return reason?.legacyMap === 'goOtherText';
      });

      return {
        meetingInvite: legacyMeeting,
        meetingBooked: meetingBookedSelected,
        eventInvite: legacyEvent,
        other:
          legacyOtherReason?.text?.trim?.() || legacyOtherReason?.text || '',
        selectedReasons,
        emailComposerRequested: shouldCompose,
      };
    };

    const onOk = () => {
      const payload = buildPayload();
      close({ ...payload, action: 'save' });
    };

    const onTask = () => {
      const payload = buildPayload();
      close({ ...payload, action: 'task' });
    };

    const onCancel = () => close(null);

    btnOk.addEventListener('click', onOk);
    btnCancel.addEventListener('click', onCancel);
    btnTask?.addEventListener('click', onTask);

    modalService.open('goModal');
  });
}
